/**
 * OnboardingAIConsent.jsx — v242.43.0 (App Store 5.1.1(i)/5.1.2(i))
 *
 * The "AI & your data" onboarding step. Placed right after Security (2FA) and
 * before Identity so the disclosure frames the whole app and consent is captured
 * before the patient reaches Home chat or any PHI-collecting step.
 *
 * This is the CAPTURE surface. Server-side fail-closed enforcement lives in the
 * API (@ai_consent_gate on POST /api/v1/ai/*, dormant until AI_CONSENT_ENFORCE).
 * On agree we record consent server-side (aiConsentAPI.record) BEFORE advancing.
 *
 * The disclosure text shown here is BUNDLED in the app (utils/aiDisclosure.js, through the
 * onboarding.aiConsent.* locale keys); it is not read from the server. The server only
 * publishes the current version (app_config.legal_content.ai_disclosure.version), and the
 * version this build sends (AI_DISCLOSURE_VERSION) must name the text it shows.
 */
import { useState } from 'react'
import { Send, Cloud, ShieldCheck, TrendingUp, FileText, Check, ChevronRight } from 'lucide-react'
import { useI18n } from '../../i18n'
import { aiConsentAPI, authAPI } from '../../services/api'
import { useUserStore } from '../../stores'
import PolicyModal from '../../components/PolicyModal'
import { useLegalContent } from '../../components/LegalMarkdown'
import { privacyConsentClaim } from '../../utils/legalDocument'
import { aiDisclosureBlocks, AI_DISCLOSURE_VERSION as DISCLOSURE_VERSION } from '../../utils/aiDisclosure'
import { consentRecordErrorText } from '../../utils/aiConsentSignal'
import orellaOIcon from '../../assets/logos/orella_o_mark.png'
import {
  OnboardingShell,
  ScreenHeader,
  ScreenIntro,
  PrimaryButton,
  Footer,
  useScrollToTop,
  NAVY,
  BLUE,
  LIGHT_BLUE,
  ICE_BLUE,
  GREEN,
  PASTEL_GREEN,
  TEXT_MUTED,
  TEXT_HINT,
  BORDER_LIGHT,
} from './onboardingPrimitives'

// ★★★ v242.153.0 · AI-DISCLOSURE-ONE-COPY-1 — THE VERSION AND THE WORDS IT NAMES
//   NOW LIVE TOGETHER, in utils/aiDisclosure.js, because Settings → AI & Data
//   needs the same paragraphs and a second copy of a consent disclosure is how a
//   patient ends up reading two different promises under one version string.
//   Re-exported here so every existing importer of this name keeps working.
export const AI_DISCLOSURE_VERSION = DISCLOSURE_VERSION

function DisclosureRow({ icon: Icon, title, children }) {
  return (
    <div className="flex" style={{ gap: 13, padding: '14px 15px', alignItems: 'flex-start' }}>
      <div
        className="flex-shrink-0 flex items-center justify-center"
        style={{ width: 34, height: 34, borderRadius: 10, background: ICE_BLUE }}
      >
        <Icon size={19} color={BLUE} strokeWidth={1.9} />
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: NAVY, marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 13.5, lineHeight: 1.5, color: TEXT_MUTED }}>{children}</div>
      </div>
    </div>
  )
}

export default function OnboardingAIConsent({ onNext, onBack, onExit, step = 2, submitError = null, mode = 'onboarding', onSignOut }) {
  const { t } = useI18n()
  useScrollToTop('ai-consent')
  // PROD-1225: mode="reconsent" is the /ai-consent/renew screen (a patient whose consent is not
  // current, after the server gate refused an AI call or the launch check found it). Same
  // disclosure, same checkbox. No step counter and no back, no Terms checkbox and no
  // recordTerms (terms are their own record, made at sign-up), an intro line above the
  // disclosure, and the decline note offers Sign out instead of "close orella".
  const reconsent = mode === 'reconsent'
  const [agreed, setAgreed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [showTerms, setShowTerms] = useState(false) // s95b: the Terms of Use are readable here too
  const [showDecline, setShowDecline] = useState(false)
  // s95: Terms + Privacy. An email sign-up ticked them on the sign-up form (pendingProfile
  // carries that); a Google/Apple sign-up never saw that form, so it is asked here. Either
  // way the acceptance is RECORDED on the server now — it used to live only in the app.
  const termsAtSignup = !!useUserStore((s) => s.pendingProfile?.termsAccepted)
  const [termsAgreed, setTermsAgreed] = useState(false)
  const termsOk = reconsent || termsAtSignup || termsAgreed
  // PROD-763 (S11): the privacy text this app holds (database, else the bundled copy); the consent
  // records which one and its version.
  const privacyDoc = useLegalContent('privacy')

  const strong = { color: NAVY, fontWeight: 600 }

  const handleContinue = async () => {
    if (!agreed || !termsOk || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      if (!reconsent) {
        await authAPI.recordTerms(termsAtSignup ? 'signup' : 'onboarding', privacyConsentClaim(privacyDoc))
      }
      await aiConsentAPI.record()
      onNext()
    } catch (e) {
      // PROD-1225 (review N1): a 409 `ai_consent_update_required` means this build's disclosure
      // text is older than the server's version; the server's own sentence (update the app and
      // agree) is shown instead of the save error, in onboarding and in re-consent mode alike.
      // Every other failure (incl. a 400 `ai_consent_version_invalid`) shows the save-error text.
      // No retry, no loop: the patient stays on this screen and nothing advances.
      // Fail-safe: do not advance without a recorded consent — enforcement is
      // fail-closed, so a silent skip would 403 the patient out of every AI feature.
      // Review P2: the 409 sentence is the localized onboarding.aiConsent.updateRequired; t()
      // echoes a missing key, so an echo is passed as '' and the server's sentence is used.
      const upd = t('onboarding.aiConsent.updateRequired')
      setError(consentRecordErrorText(e, t('onboarding.aiConsent.saveError', {
        defaultValue: 'Could not save your choice. Please check your connection and try again.',
      }), upd === 'onboarding.aiConsent.updateRequired' ? '' : upd))
      setSubmitting(false)
    }
  }

  return (
    <OnboardingShell>
      {reconsent
        ? <ScreenHeader showBack={false} showProgress={false} />
        : <ScreenHeader onBack={onBack} step={step} onExit={onExit} />}

      <ScreenIntro
        eyebrow={t('onboarding.aiConsent.eyebrow', { defaultValue: 'AI & your data' })}
        title={t('onboarding.aiConsent.title', { defaultValue: 'How orella uses AI' })}
        required
        dialogue={t('onboarding.aiConsent.dialogue', {
          defaultValue:
            "I'm an AI assistant. To help prepare your visit, the details you share get processed by a secure AI service. Here's exactly what's sent and who handles it — the choice to continue is yours.",
        })}
      />

      <div style={{ padding: '16px 20px 4px' }}>
        {reconsent && (
          <p data-reconsent-intro style={{ fontSize: 13.5, lineHeight: 1.5, color: NAVY, fontWeight: 600, margin: '0 2px 12px' }}>
            {t('onboarding.aiConsent.reconsentIntro', {
              defaultValue: 'Please review how Orella uses AI and agree to keep using Orella.',
            })}
          </p>
        )}
        <div
          style={{
            border: `1.5px solid ${LIGHT_BLUE}`,
            borderRadius: 16,
            overflow: 'hidden',
            marginBottom: 14,
          }}
        >
          {/* ★★★ v242.153.0 — rendered from utils/aiDisclosure.js. The three blocks
              used to be written out here and NOWHERE ELSE, which is why Settings →
              AI & Data could only show two bullets. The words are shared; the icons,
              the card chrome and the emphasis styling stay each surface's own.
              TF-08 (v242.61.0): the "Covered by a HIPAA Business Associate Agreement"
              pill was removed at Dr. Rami's request — the `who` block already says the
              data stays inside the protected environment. The i18n key
              `onboarding.aiConsent.baa` is deliberately LEFT IN the locale files so
              restoring it is one line and no re-translation.
              ★★ CORRECTION, v242.154.0: the v242.87.0 comment that stood here said
              "No locale defines onboarding.aiConsent.* — all eleven render these inline
              defaults." THAT HAS NOT BEEN TRUE SINCE THE NAMESPACE WAS TRANSLATED: en
              and es both define all 19 keys. It mattered the moment this copy became
              shared, because it is the difference between Settings → AI & Data showing
              a Spanish patient the disclosure in Spanish and showing it in English.
              A comment that has aged into a false statement is measured, not inherited —
              scripts/check-checkup-copy.mjs now asserts the key set instead. */}
          {aiDisclosureBlocks(t, { surface: 'onboarding' }).map((block, bi) => {
            const Icon = { send: Send, cloud: Cloud, shield: ShieldCheck, trending: TrendingUp }[block.icon]
            return (
              <div key={block.id} style={bi === 0 ? undefined : { borderTop: `1px solid ${BORDER_LIGHT}` }}>
                <DisclosureRow icon={Icon} title={block.title}>
                  {block.lines.map((ln, li) => (
                    <span key={li} style={ln.strong ? strong : undefined}>{ln.text}{li < block.lines.length - 1 ? ' ' : ''}</span>
                  ))}
                </DisclosureRow>
              </div>
            )
          })}
        </div>

        <div className="flex flex-wrap items-center" style={{ columnGap: 18, rowGap: 2, padding: '2px 2px 12px' }}>
          <button
            onClick={() => setShowPrivacy(true)}
            className="flex items-center"
            style={{ gap: 6, fontSize: 13.5, color: BLUE, fontWeight: 600, padding: 0, background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <FileText size={15} color={BLUE} strokeWidth={2} />
            {t('onboarding.aiConsent.privacyLink', { defaultValue: 'Read our full Privacy Policy' })}
          </button>
          <button
            onClick={() => setShowTerms(true)}
            className="flex items-center"
            style={{ gap: 6, fontSize: 13.5, color: BLUE, fontWeight: 600, padding: 0, background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <FileText size={15} color={BLUE} strokeWidth={2} />
            {t('onboarding.aiConsent.termsLink', 'Read our Terms of Use')}
          </button>
        </div>

        <div
          onClick={() => setAgreed((v) => !v)}
          className="flex cursor-pointer transition"
          style={{
            gap: 12, alignItems: 'flex-start', padding: '14px 15px', borderRadius: 14,
            border: `1.5px solid ${agreed ? GREEN : LIGHT_BLUE}`,
            background: agreed ? 'rgba(213,239,211,0.25)' : '#fff',
          }}
        >
          <div
            className="flex-shrink-0 flex items-center justify-center transition"
            style={{
              width: 26, height: 26, borderRadius: 8, marginTop: 1,
              background: agreed ? GREEN : '#F2F4F7',
              border: `1.5px solid ${agreed ? GREEN : '#D3DEE7'}`,
            }}
          >
            {agreed && <Check size={15} color="#fff" strokeWidth={3} />}
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.45, color: NAVY }}>
            {t('onboarding.aiConsent.checkbox', {
              defaultValue:
                'I understand and agree to orella using AI to process the information I share, as described above.',
            })}
          </div>
        </div>

        {!reconsent && !termsAtSignup && (
          <div
            onClick={() => setTermsAgreed((v) => !v)}
            className="flex cursor-pointer transition"
            style={{
              gap: 12, alignItems: 'flex-start', padding: '14px 15px', borderRadius: 14, marginTop: 10,
              border: `1.5px solid ${termsAgreed ? GREEN : LIGHT_BLUE}`,
              background: termsAgreed ? 'rgba(213,239,211,0.25)' : '#fff',
            }}
          >
            <div
              className="flex-shrink-0 flex items-center justify-center transition"
              style={{
                width: 26, height: 26, borderRadius: 8, marginTop: 1,
                background: termsAgreed ? GREEN : '#F2F4F7',
                border: `1.5px solid ${termsAgreed ? GREEN : '#D3DEE7'}`,
              }}
            >
              {termsAgreed && <Check size={15} color="#fff" strokeWidth={3} />}
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.45, color: NAVY }}>
              {t('onboarding.aiConsent.termsCheckbox', 'I agree to the Terms of Use and the Privacy Policy.')}
            </div>
          </div>
        )}

        {showDecline && (
          <div
            style={{
              fontSize: 13, lineHeight: 1.55, color: TEXT_MUTED, background: '#F7FAFC',
              border: `1px solid ${BORDER_LIGHT}`, borderRadius: 12, padding: '12px 14px', marginTop: 8,
            }}
          >
            {reconsent
              ? t('onboarding.aiConsent.reconsentDeclineBody', {
                defaultValue:
                  "Orella uses AI throughout, so the app can't work without this. You can sign out now; your account and cases stay, and this screen will appear the next time you sign in.",
              })
              : t('onboarding.aiConsent.declineBody', {
                defaultValue:
                  "orella uses AI throughout to prepare your visit, so the app can't function without this. You can decline and close orella, or agree now and withdraw consent later in Settings → AI & Data Consent.",
              })}
            {reconsent && onSignOut && (
              <button
                type="button"
                data-reconsent-signout
                onClick={onSignOut}
                style={{
                  display: 'block', width: '100%', marginTop: 10, padding: '10px 12px', borderRadius: 10,
                  background: '#fff', border: `1.5px solid ${LIGHT_BLUE}`, color: NAVY, fontSize: 13.5,
                  fontWeight: 600, cursor: 'pointer',
                }}
              >
                {t('onboarding.aiConsent.reconsentSignOut', { defaultValue: 'Sign out' })}
              </button>
            )}
          </div>
        )}
        <button
          onClick={() => setShowDecline((v) => !v)}
          style={{
            display: 'block', width: '100%', textAlign: 'center', fontSize: 13, color: TEXT_MUTED,
            textDecoration: 'underline', padding: 12, background: 'none', border: 'none', cursor: 'pointer',
          }}
        >
          {t('onboarding.aiConsent.declineLink', { defaultValue: "What if I don't agree?" })}
        </button>

        {error && (
          <p style={{ fontSize: 13, color: '#C0392B', textAlign: 'center', marginTop: 2 }}>{error}</p>
        )}
      </div>

      {/* PROD-1194 review M2: the sign-up finish error (OnboardingFlow) is pinned in the bar above
          "Agree & Continue"; as a fixed toast it lay over the button, which no scroll can free. */}
      <Footer note={submitError ? (
        <div role="alert" data-submit-error className="rounded-[12px]"
          style={{ padding: '10px 14px', marginBottom: 10, background: '#FEE2E2', border: '1px solid #DC2626', color: '#7F1D1D', fontSize: 12.5 }}>
          {submitError}
        </div>
      ) : null}>
        <PrimaryButton onClick={handleContinue} disabled={!agreed || !termsOk || submitting}>
          {submitting
            ? t('onboarding.aiConsent.saving', { defaultValue: 'Saving…' })
            : t('onboarding.aiConsent.continue', { defaultValue: 'Agree & Continue' })}
        </PrimaryButton>
        <div style={{ textAlign: 'center', fontSize: 12.5, color: TEXT_HINT, marginTop: 14 }}>
          {t('onboarding.welcome.security', { defaultValue: 'HIPAA-secured · Encrypted at rest' })}
        </div>
      </Footer>

      {showPrivacy && (
        <PolicyModal
          title={t('legal.privacyTitle', { defaultValue: 'Privacy Policy' })}
          type="privacy"
          onClose={() => setShowPrivacy(false)}
        />
      )}
      {showTerms && (
        <PolicyModal
          title={t('legal.termsTitle', 'Terms of Use')}
          type="terms"
          onClose={() => setShowTerms(false)}
        />
      )}
    </OnboardingShell>
  )
}
